import React, { useCallback, useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { FileEdit, Eye } from 'lucide-react';
import CVForm from './CVForm';
import BuilderHeader from './BuilderHeader';
import BuilderPreview from './BuilderPreview';
import BuilderModals from './BuilderModals';
import BuilderTour from './BuilderTour';
import {
    BUILDER_TOUR_STORAGE_KEY,
    TourFormStep,
    TourStep,
} from './builderTourSteps';
import { useAuth } from '../../context/AuthContext';
import { useCVBuilder } from '../../hooks/useCVBuilder';
import { DEV_BYPASS_PAYMENT } from '../../config/dev';
import { paymentCredits, publicTemplates } from '../../services/adminService';
import { API_URL } from '../../services/api';
import { TEMPLATE_CONFIGS } from '../../types/cv';
import { trackEvent } from '../../utils/analytics';

type MobileTab = 'form' | 'preview';

const CVBuilder: React.FC = () => {
    const { logout, token } = useAuth();
    const [searchParams] = useSearchParams();
    const cvIdFromUrl = searchParams.get('id');
    const templateFromUrl = searchParams.get('template');

    const {
        data,
        template,
        setTemplate,
        savedCVId,
        isSaving,
        hasPaid,
        setHasPaid,
        showSaveSuccess,
        handleChange,
        handleLayoutChange,
        handleMoveItem,
        handleMoveSection,
        handleSetPageCount,
        handleSaveCV,
        handleSelectCV,
        handleNewCV,
    } = useCVBuilder(cvIdFromUrl, token, templateFromUrl);

    const [showPaymentModal, setShowPaymentModal] = useState(false);
    const [showHistoryModal, setShowHistoryModal] = useState(false);
    const [freeCredits, setFreeCredits] = useState(0);
    const [downloadPrice, setDownloadPrice] = useState(750);
    const [ordering, setOrdering] = useState(false);
    const [downloading, setDownloading] = useState(false);
    const [mobileTab, setMobileTab] = useState<MobileTab>('form');
    // Comment le téléchargement en cours a été débloqué — un crédit gratuit et
    // un paiement mettent tous les deux hasPaid à true, donc l'événement GA
    // unlock_method a besoin de cette distinction séparée pour rester fiable.
    const [unlockMethod, setUnlockMethod] = useState<'paid' | 'free' | null>(null);
    const [tourOpen, setTourOpen] = useState(false);
    const [tourFormStep, setTourFormStep] = useState<TourFormStep | null>(null);
    const [tourOpenOrganizer, setTourOpenOrganizer] = useState(false);

    const refreshDownloadPrice = useCallback(() => {
        fetch(`${API_URL}/payment/methods`)
            .then((r) => r.json())
            .then((data) => {
                if (data?.amount && Number(data.amount) > 0) {
                    setDownloadPrice(Number(data.amount));
                }
            })
            .catch(() => undefined);
    }, []);

    useEffect(() => {
        // cvIdFromUrl absent = nouveau CV (venant du choix de modèle), pas une reprise de brouillon.
        if (!cvIdFromUrl) {
            trackEvent('cv_creation_started', { template_id: templateFromUrl || '' });
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        publicTemplates('cv')
            .then((list: any[]) => {
                const opts = (list || []).map((t) => t.slug || t.id).filter(Boolean);
                const known = opts.length ? opts : TEMPLATE_CONFIGS.map((t) => t.id);
                if (known.length && !known.includes(template)) {
                    setTemplate(known[0]);
                }
            })
            .catch(() => {
                /* garder le template courant */
            });

        if (!DEV_BYPASS_PAYMENT) {
            paymentCredits
                .get()
                .then((r) => setFreeCredits(r.freeDownloadsRemaining || 0))
                .catch(() => setFreeCredits(0));
        }

        refreshDownloadPrice();
    }, []); // eslint-disable-line react-hooks/exhaustive-deps

    /** Prix backoffice : rafraîchir régulièrement + au retour sur l’onglet */
    useEffect(() => {
        refreshDownloadPrice();
        const onFocus = () => refreshDownloadPrice();
        const onVis = () => {
            if (document.visibilityState === 'visible') refreshDownloadPrice();
        };
        window.addEventListener('focus', onFocus);
        document.addEventListener('visibilitychange', onVis);
        const interval = window.setInterval(refreshDownloadPrice, 8000);
        return () => {
            window.removeEventListener('focus', onFocus);
            document.removeEventListener('visibilitychange', onVis);
            window.clearInterval(interval);
        };
    }, [refreshDownloadPrice]);

    /** Première visite (ou ?tour=1) : lancer le guide automatiquement */
    useEffect(() => {
        const force = searchParams.get('tour') === '1';
        try {
            if (!force && localStorage.getItem(BUILDER_TOUR_STORAGE_KEY)) return;
        } catch {
            /* ignore */
        }
        const t = window.setTimeout(() => setTourOpen(true), 700);
        return () => window.clearTimeout(t);
    }, [searchParams]);

    const markTourDone = useCallback(() => {
        try {
            localStorage.setItem(BUILDER_TOUR_STORAGE_KEY, '1');
        } catch {
            /* ignore */
        }
        setTourOpen(false);
        setTourFormStep(null);
        setTourOpenOrganizer(false);
    }, []);

    const startTour = useCallback(() => {
        setTourFormStep(null);
        setTourOpenOrganizer(false);
        setMobileTab('form');
        setTourOpen(true);
    }, []);

    const handleTourStepChange = useCallback((step: TourStep) => {
        if (step.mobileTab) setMobileTab(step.mobileTab);
        setTourFormStep(step.formStep ?? null);
        setTourOpenOrganizer(!!step.openOrganizer);
    }, []);

    /** Affiche l’aperçu puis imprime (fallback DEV uniquement si l’API PDF échoue). */
    const printWithPreview = useCallback(() => {
        setMobileTab('preview');
        window.setTimeout(() => {
            window.print();
        }, 150);
    }, []);

    const downloadServerPdf = useCallback(
        async (cvId: string) => {
            const res = await fetch(`${API_URL}/cv/${cvId}/pdf`, {
                method: 'POST',
                headers: {
                    Authorization: `Bearer ${token}`,
                    'Content-Type': 'application/json',
                },
            });
            if (!res.ok) {
                const err = await res.json().catch(() => ({}));
                throw new Error(err.error || `Échec PDF (${res.status})`);
            }
            const blob = await res.blob();
            const url = URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = url;
            a.download = `${(data.title || 'CV').replace(/[^\w\s\-]/g, '').trim() || 'CV'}.pdf`;
            document.body.appendChild(a);
            a.click();
            a.remove();
            URL.revokeObjectURL(url);
        },
        [token, data.title]
    );

    const handleOrderCV = async (e: React.MouseEvent) => {
        e.preventDefault();
        if (DEV_BYPASS_PAYMENT) {
            await handleDownload();
            return;
        }
        if (ordering) return;
        setOrdering(true);
        try {
            let cvId = savedCVId;
            if (!cvId) {
                cvId = await handleSaveCV(false);
            }
            if (!cvId) {
                alert('Enregistrez d’abord votre CV.');
                return;
            }

            if (freeCredits > 0) {
                const result = await paymentCredits.use(cvId);
                const remaining = result.freeDownloadsRemaining ?? freeCredits - 1;
                setFreeCredits(remaining);
                setHasPaid(true);
                setUnlockMethod('free');
                trackEvent('free_download_used', {
                    document_type: 'cv',
                    credits_remaining: remaining,
                });
                return;
            }

            refreshDownloadPrice();
            setShowPaymentModal(true);
        } catch (err: any) {
            if (String(err.message || '').includes('Aucun téléchargement')) {
                refreshDownloadPrice();
                setShowPaymentModal(true);
            } else {
                alert(err.message || 'Erreur');
            }
        } finally {
            setOrdering(false);
        }
    };

    const handleDownload = async () => {
        if (downloading) return;
        setDownloading(true);
        try {
            const cvId = await handleSaveCV(false);
            if (!cvId) {
                alert('Enregistrez d’abord votre CV.');
                return;
            }
            await downloadServerPdf(cvId);
            trackEvent('document_download', {
                document_type: 'cv',
                format: 'pdf',
                template_id: template,
                unlock_method: unlockMethod || 'dev_bypass',
            });
            if (!DEV_BYPASS_PAYMENT) {
                setHasPaid(false);
                setUnlockMethod(null);
            }
        } catch (err: any) {
            console.error(err);
            if (DEV_BYPASS_PAYMENT) {
                printWithPreview();
            } else {
                alert(err.message || 'Impossible de générer le PDF. Réessayez.');
            }
        } finally {
            setDownloading(false);
        }
    };

    return (
        <div className="h-[100dvh] md:min-h-screen md:h-auto bg-slate-50 flex flex-col overflow-hidden md:overflow-visible">
            <div className="no-print shrink-0">
                <BuilderHeader
                    hasPaid={hasPaid}
                    setHasPaid={setHasPaid}
                    isSaving={isSaving || ordering}
                    isDownloading={downloading}
                    showSaveSuccess={showSaveSuccess}
                    freeDownloadsRemaining={freeCredits}
                    downloadPrice={downloadPrice}
                    onSave={handleSaveCV}
                    onOrder={handleOrderCV}
                    onDownload={handleDownload}
                    setShowHistoryModal={setShowHistoryModal}
                    logout={logout}
                    onHelp={startTour}
                />
            </div>

            {/* Onglets mobile uniquement */}
            <div className="no-print md:hidden shrink-0 border-b border-slate-200 bg-white px-3 pt-2" data-tour="mobile-tabs">
                <div className="flex rounded-xl bg-slate-100 p-1 gap-1" role="tablist" aria-label="Vue mobile">
                    <button
                        type="button"
                        role="tab"
                        aria-selected={mobileTab === 'form'}
                        onClick={() => setMobileTab('form')}
                        className={`flex-1 flex items-center justify-center gap-2 rounded-lg py-2.5 text-sm font-semibold transition-colors ${
                            mobileTab === 'form'
                                ? 'bg-white text-indigo-700 shadow-sm'
                                : 'text-slate-500'
                        }`}
                    >
                        <FileEdit className="w-4 h-4" />
                        Formulaire
                    </button>
                    <button
                        type="button"
                        role="tab"
                        aria-selected={mobileTab === 'preview'}
                        onClick={() => {
                            setMobileTab('preview');
                            trackEvent('cv_preview_viewed');
                        }}
                        className={`flex-1 flex items-center justify-center gap-2 rounded-lg py-2.5 text-sm font-semibold transition-colors ${
                            mobileTab === 'preview'
                                ? 'bg-white text-indigo-700 shadow-sm'
                                : 'text-slate-500'
                        }`}
                    >
                        <Eye className="w-4 h-4" />
                        Aperçu
                    </button>
                </div>
            </div>

            <main className="flex-1 flex flex-col md:flex-row overflow-hidden min-h-0">
                <div
                    className={`no-print overflow-y-auto p-4 sm:p-6 md:p-8 border-slate-200 md:border-r md:w-[40%] md:shrink-0 ${
                        mobileTab === 'form' ? 'flex flex-col flex-1' : 'hidden'
                    } md:flex`}
                >
                    <div className="max-w-2xl mx-auto w-full space-y-6 md:space-y-8 animate-fade-in pb-8">
                        <CVForm
                            data={data}
                            onChange={handleChange}
                            template={template}
                            tourFocusStep={tourFormStep}
                        />
                    </div>
                </div>

                <div
                    className={`min-w-0 flex-1 overflow-hidden ${
                        mobileTab === 'preview' ? 'flex' : 'hidden'
                    } md:flex print:!block print:!overflow-visible`}
                >
                    <BuilderPreview
                        data={data}
                        template={template}
                        onMoveItem={handleMoveItem}
                        onMoveSection={handleMoveSection}
                        onSetPageCount={handleSetPageCount}
                        onReplaceData={handleLayoutChange}
                        tourOpenOrganizer={tourOpen && tourOpenOrganizer}
                    />
                </div>
            </main>

            <div className="no-print">
                <BuilderModals
                    showPaymentModal={!DEV_BYPASS_PAYMENT && showPaymentModal}
                    setShowPaymentModal={setShowPaymentModal}
                    showHistoryModal={showHistoryModal}
                    setShowHistoryModal={setShowHistoryModal}
                    savedCVId={savedCVId}
                    amount={downloadPrice}
                    templateId={template}
                    templateName={TEMPLATE_CONFIGS.find((t) => t.id === template)?.name || template}
                    onSave={handleSaveCV}
                    onSuccessPayment={() => {
                        setHasPaid(true);
                        setUnlockMethod('paid');
                    }}
                    onSelectCV={handleSelectCV}
                    onNewCV={handleNewCV}
                />
            </div>

            <BuilderTour open={tourOpen} onClose={markTourDone} onStepChange={handleTourStepChange} />
        </div>
    );
};

export default CVBuilder;
